import type React from "react";
import { Suspense, useEffect } from "react";
import { CAMPAIGN_PHASES } from "../game/campaign";
import { useGameStore } from "../game/useGameStore";
import { primaryButton, typography } from "./theme";
import { ShojiBacking, PanelText, PanelRule, PanelErrorBoundary } from "./panel3d";

export function StoryBeat() {
  const phase = useGameStore((s) => s.phase);
  const lastRunPhaseIndex = useGameStore((s) => s.lastRunPhaseIndex);
  const finishStory = useGameStore((s) => s.finishStory);
  const story = CAMPAIGN_PHASES[lastRunPhaseIndex]?.story;

  useEffect(() => {
    if (phase !== "story") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") finishStory();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, finishStory]);

  // Defensive fallback — the app only routes here when a phase defines a
  // story beat, but never leave the player stuck if that assumption breaks.
  useEffect(() => {
    if (phase === "story" && !story) finishStory();
  }, [phase, story, finishStory]);

  if (phase !== "story" || !story) return null;

  return (
    <group position={[0, 1.6, -1.7]}>
      <PanelErrorBoundary label="StoryBeat">
        <Suspense fallback={null}>
          <ShojiBacking width={1.9} height={0.9}>
            <PanelText tier="kicker" position={[0, 0.34, 0]}>
              {story.speaker.toUpperCase()}
            </PanelText>
            <PanelRule length={0.5} position={[0, 0.29, 0]} />
            {/* anchorY="top" so longer lines (now two sentences — the
                phase-just-finished beat plus a bridge into the next phase)
                grow downward only, instead of expanding upward into the
                speaker label above. */}
            <PanelText
              tier="seiji"
              position={[0, 0.2, 0]}
              anchorY="top"
              maxWidth={1.7}
              textAlign="center"
            >
              {story.line}
            </PanelText>
            <PanelText tier="footerHint" position={[0, -0.36, 0]}>
              ENTER / A · SKIP
            </PanelText>
          </ShojiBacking>
        </Suspense>
      </PanelErrorBoundary>
    </group>
  );
}

export function StoryOverlay() {
  const phase = useGameStore((s) => s.phase);
  const lastRunPhaseIndex = useGameStore((s) => s.lastRunPhaseIndex);
  const finishStory = useGameStore((s) => s.finishStory);
  const story = CAMPAIGN_PHASES[lastRunPhaseIndex]?.story;

  if (phase !== "story" || !story) return null;

  return (
    <div style={storyOverlayActions}>
      <button onClick={finishStory} style={storyPrimaryAction}>
        Continue
      </button>
    </div>
  );
}

const storyOverlayActions: React.CSSProperties = {
  position: "fixed",
  bottom: 40,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
};

const storyPrimaryAction: React.CSSProperties = {
  ...primaryButton,
  ...typography.ui,
  padding: "14px 28px",
  fontSize: 15,
};
